import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Collapse,
  Row,
  Col,
  Carousel,
  CarouselItem,
  CarouselIndicators,
  Badge,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import { ChevronDown, ChevronRight, Edit, Trash2 } from "react-feather";
import "./ProductAccordionList.scss";
import { ChevronUp } from "lucide-react";

/**
 * Props:
 *   data: Array of product objects
 *   onEdit:     (product) => void
 *   onDelete:   (product) => void
 *   currentPage: number (1-based index)
 *   pageSize:    number
 *   totalItems:  number
 *   onPageChange: (newPage: number) => void
 */
const ProductAccordionList = ({
  data,
  onEdit,
  onUpdateStatus,
  onDelete,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
}) => {
  const [openIndex, setOpenIndex] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState({});
  const FILE_BASE_URL = process.env.REACT_APP_FILE_BASE_URL || process.env.REACT_APP_BASE_URL || "http://localhost:4001";

  const toggleAccordion = (idx) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  const nextImage = (productId, images) => {
    setCarouselIndex((prev) => {
      const current = prev[productId] ?? 0;
      return {
        ...prev,
        [productId]: current === images.length - 1 ? 0 : current + 1,
      };
    });
  };

  const prevImage = (productId, images) => {
    setCarouselIndex((prev) => {
      const current = prev[productId] ?? 0;
      return {
        ...prev,
        [productId]:
          current === 0 ? images.length - 1 : current - 1,
      };
    });
  };

  const goToImage = (productId, newIndex) => {
    setCarouselIndex((prev) => ({
      ...prev,
      [productId]: newIndex,
    }));
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalItems / pageSize);

  const changePage = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === currentPage) return;
    onPageChange(newPage);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const visiblePages = 3; // # pages before and after current

    const startPage = Math.max(2, currentPage - visiblePages);
    const endPage = Math.min(totalPages - 1, currentPage + visiblePages);

    // Always show page 1
    pages.push(
      <PaginationItem active={currentPage === 1} key={1}>
        <PaginationLink onClick={() => changePage(1)}>1</PaginationLink>
      </PaginationItem>
    );

    // Left ellipsis
    if (startPage > 2) {
      pages.push(
        <PaginationItem disabled key="start-ellipsis">
          <PaginationLink>...</PaginationLink>
        </PaginationItem>
      );
    }

    // Middle pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem active={i === currentPage} key={i}>
          <PaginationLink onClick={() => changePage(i)}>{i}</PaginationLink>
        </PaginationItem>
      );
    }

    // Right ellipsis
    if (endPage < totalPages - 1) {
      pages.push(
        <PaginationItem disabled key="end-ellipsis">
          <PaginationLink>...</PaginationLink>
        </PaginationItem>
      );
    }

    // Always show last page if more than 1
    if (totalPages > 1) {
      pages.push(
        <PaginationItem active={currentPage === totalPages} key={totalPages}>
          <PaginationLink onClick={() => changePage(totalPages)}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return (
      <div className="d-flex justify-content-center mt-4">
        <Pagination>
          <PaginationItem disabled={currentPage === 1}>
            <PaginationLink first onClick={() => changePage(1)} />
          </PaginationItem>
          <PaginationItem disabled={currentPage === 1}>
            <PaginationLink previous onClick={() => changePage(currentPage - 1)} />
          </PaginationItem>

          {pages}

          <PaginationItem disabled={currentPage === totalPages}>
            <PaginationLink next onClick={() => changePage(currentPage + 1)} />
          </PaginationItem>
          <PaginationItem disabled={currentPage === totalPages}>
            <PaginationLink last onClick={() => changePage(totalPages)} />
          </PaginationItem>
        </Pagination>
      </div>
    );
  };

  return (
    <div className="product-accordion-list">
      {data.map((product, idx) => {
        const isOpen = openIndex === idx;
        const images = product.images || [];
        const activeIdx = carouselIndex[product._id] ?? 0;

        return (
          <Card key={product._id} className={`mb-3 shadow-sm ${isOpen ? "expanded" : ""}`}>
            <CardHeader
              onClick={() => toggleAccordion(idx)}
              className="d-flex justify-content-between align-items-center header-custom"
            >
              {/* Left side: just the name */}
              <h5
                className="mb-0 text-truncate"
                style={{ maxWidth: "300px" }}
              >
                {product.name}
              </h5>

              {/* Right side: status badge, discount badge, toggle icon */}
              <div className="d-flex align-items-center">
                <Badge
                  color={product.status === "active" ? "success" : "danger"}
                  className="text-capitalize me-2"
                  pill
                  style={{ cursor: "pointer" }}
                  onClick={() => onUpdateStatus(product)}
                >
                  {product.status}
                </Badge>

                <Badge
                  color="warning"
                  className="text-capitalize me-3"
                  pill
                >
                  {product.discountType === "percentage"
                    ? `${product.discount}% off`
                    : `KWD ${product.discount?.toFixed(2)} off`}
                </Badge>

                {isOpen ? (
                  <ChevronDown size={20} className="toggle-icon" />
                ) : (
                  <ChevronUp size={20} className="toggle-icon" />
                )}
              </div>
            </CardHeader>


            <Collapse isOpen={isOpen}>
              <CardBody>
                <Row>
                  {/* Left column: Image carousel */}
                  <Col md={5} className="border-end ps-0 pe-3">
                    {images.length > 0 ? (
                      <div className="position-relative">
                        <Carousel
                          activeIndex={activeIdx}
                          next={() => nextImage(product._id, images)}
                          previous={() => prevImage(product._id, images)}
                        >
                          <CarouselIndicators
                            items={images.map((src) => ({ src }))}
                            activeIndex={activeIdx}
                            onClickHandler={(newIndex) =>
                              goToImage(product._id, newIndex)
                            }
                          />
                          {images.map((imgUrl, i) => (
                            <CarouselItem key={i}>
                              <img
                                src={FILE_BASE_URL + "/" + imgUrl}
                                alt={`Slide ${i}`}
                                className="d-block w-100 rounded"
                                style={{
                                  maxHeight: "300px",
                                  objectFit: "contain",
                                }}
                              />
                            </CarouselItem>
                          ))}
                        </Carousel>
                        <Button
                          className="carousel-btn left"
                          onClick={() =>
                            prevImage(product._id, images)
                          }
                        >
                          ‹
                        </Button>
                        <Button
                          className="carousel-btn right"
                          onClick={() =>
                            nextImage(product._id, images)
                          }
                        >
                          ›
                        </Button>
                      </div>
                    ) : (
                      <div className="text-muted text-center py-5">
                        No Images Available
                      </div>
                    )}
                  </Col>

                  {/* Right column: Details */}
                  <Col md={7} className="ps-4">
                    <Row>
                      <Col xs={12} className="mb-2">
                        <strong>Category:</strong>{" "}
                        {product.category?.name || "N/A"}
                      </Col>
                      <Col xs={12} className="mb-2">
                        <strong>Sub Category:</strong>{" "}
                        {product.subCategory?.name || "N/A"}
                      </Col>
                      <Col xs={6} className="mb-2">
                        <strong>Base Price:</strong> KWD
                        {product.basePrice?.toFixed(2)}
                      </Col>
                      <Col xs={6} className="mb-2">
                        <strong>Final Price:</strong> KWD
                        {product.finalPrice?.toFixed(2)}
                      </Col>
                      <Col xs={6} className="mb-2">
                        <strong>Stock:</strong>{" "}
                        <Badge
                          color={
                            product.stock < 10 ? "danger" : "success"
                          }
                        >
                          {product.stock}{" "}
                          {product.stock < 10 ? "(Low)" : ""}
                        </Badge>
                      </Col>
                      <Col xs={12} className="mb-3">
                        <strong>Description:</strong>{" "}
                        {product.description}
                      </Col>

                      {/* Colors as swatches */}
                      {Array.isArray(product.colors) &&
                        product.colors.length > 0 && (
                          <Col xs={12} className="mb-3">
                            <strong>Colors:</strong>
                            <div className="d-flex flex-wrap mt-1">
                              {product.colors.map((c) => (
                                <div
                                  key={c._id}
                                  className="color-swatch me-2 mb-2"
                                  title={`Stock: ${c.stock}`}
                                  style={{
                                    backgroundColor: c.color,
                                  }}
                                ></div>
                              ))}
                            </div>
                          </Col>
                        )}

                      {/* Sizes table */}
                      {Array.isArray(product.sizes) &&
                        product.sizes.length > 0 && (
                          <Col xs={12} className="mb-3">
                            <strong>Sizes:</strong>
                            <div className="table-responsive">
                              <table className="table table-sm">
                                <thead>
                                  <tr>
                                    <th>Size</th>
                                    <th>Stock</th>
                                    <th>Price</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {product.sizes.map((s) => (
                                    <tr key={s._id}>
                                      <td>{s.size}</td>
                                      <td>{s.stock}</td>
                                      <td>KWD {s.price.toFixed(2)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Col>
                        )}

                      {/* Timestamps */}
                      <Col xs={12} className="mb-3 text-muted small">
                        Created:{" "}
                        {new Date(
                          product.createdAt
                        ).toLocaleDateString()}{" "}
                        | Updated:{" "}
                        {new Date(
                          product.updatedAt
                        ).toLocaleDateString()}
                      </Col>

                      {/* Edit / Delete Buttons */}
                      <Col xs={12} className="d-flex gap-2 align-items-center">
                        <button
                          onClick={() => onEdit(product)}
                          style={{
                            backgroundColor: "#33bdbf",
                            color: "#fff",
                            border: "none",
                            borderRadius: "4px",
                            padding: "6px 12px",
                            fontSize: "12px",
                            fontWeight: "500",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px"
                          }}
                        >
                          <Edit size={14} /> Edit
                        </button>

                        <button
                          onClick={() => onDelete(product)}
                          style={{
                            backgroundColor: "#dc3545",
                            color: "#fff",
                            border: "none",
                            borderRadius: "4px",
                            padding: "6px 12px",
                            fontSize: "12px",
                            fontWeight: "500",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px"
                          }}
                        >
                          <Trash2 size={14} /> Delete
                        </button>

                        <div style={{ display: "flex", alignItems: "center", minWidth: "100px", marginLeft: "8px" }}>
                          <div style={{ position: "relative" }}>
                            <input
                              type="checkbox"
                              id={`toggleStatus-${product._id}`}
                              checked={product.status === "active"}
                              onChange={() => onUpdateStatus(product)}
                              style={{
                                width: "44px",
                                height: "24px",
                                appearance: "none",
                                backgroundColor: product.status === "active" ? "#198754" : "#adb5bd",
                                borderRadius: "12px",
                                position: "relative",
                                cursor: "pointer",
                                outline: "none",
                                transition: "background-color 0.3s ease"
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                top: "2px",
                                left: product.status === "active" ? "22px" : "2px",
                                width: "20px",
                                height: "20px",
                                backgroundColor: "#fff",
                                borderRadius: "50%",
                                transition: "left 0.3s ease",
                                pointerEvents: "none"
                              }}
                            />
                          </div>
                          <span
                            style={{
                              marginLeft: "8px",
                              fontSize: "12px",
                              fontWeight: "600",
                              textTransform: "capitalize",
                              color: product.status === "active" ? "#198754" : "#adb5bd"
                            }}
                          >
                            {product.status}
                          </span>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </CardBody>
            </Collapse>
          </Card>
        );
      })}

      {/* Pagination */}
      {renderPagination()}
    </div>
  );
};

export default ProductAccordionList;
